
import { useState } from 'react';

export default function ServiceOfferings() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const services = [
    {
      icon: 'ri-user-heart-line',
      title: 'Potential Customer Visit',
      description: 'Facilitate meaningful connections between you and your potential customers through organized visits and business meetings.',
      features: [
        'Customer meeting coordination',
        'Business presentation setup',
        'Professional networking facilitation',
        'Follow-up support and documentation'
      ]
    },
    {
      icon: 'ri-building-line',
      title: 'US Office Space with Conference Room and Meeting Room',
      description: 'Professional office facilities equipped with modern conference and meeting rooms for your business operations.',
      features: [
        'Fully equipped conference rooms',
        'Private meeting spaces',
        'High-speed internet and AV equipment',
        'Professional business address'
      ]
    },
    {
      icon: 'ri-mail-line',
      title: 'Virtual Mail Service',
      description: 'Comprehensive mail handling service providing you with a prestigious US business address and mail management.',
      features: [
        'US business address',
        'Mail receiving and forwarding',
        'Package handling service',
        'Digital mail notifications'
      ]
    },
    {
      icon: 'ri-handshake-line',
      title: 'Partners with Insurance and Accountants for Business Setup',
      description: 'Strategic partnerships with trusted insurance providers and certified accountants to streamline your business establishment.',
      features: [
        'Business insurance consultation',
        'Accounting and bookkeeping services',
        'Legal compliance assistance',
        'Tax planning and preparation'
      ]
    }
  ];

  const officeImages = [
    {
      src: 'https://static.readdy.ai/image/e4ceb88f54bc3f3d20d8be6f49e219a4/06545a794fca2ad14520f3dcfa9020a4.jpeg',
      alt: 'Modern office building exterior'
    },
    {
      src: 'https://static.readdy.ai/image/e4ceb88f54bc3f3d20d8be6f49e219a4/9954588cb0aa622dc2db1d8eb1c06308.jpeg',
      alt: 'Office entrance and lobby'
    },
    {
      src: 'https://static.readdy.ai/image/e4ceb88f54bc3f3d20d8be6f49e219a4/dff580d375b7ac1dd8677666e625b82a.jpeg',
      alt: 'Executive conference room'
    },
    {
      src: 'https://static.readdy.ai/image/e4ceb88f54bc3f3d20d8be6f49e219a4/6193ceaaf5cef3ae5e90f113b765b314.jpeg',
      alt: 'Business meeting room'
    },
    {
      src: 'https://static.readdy.ai/image/e4ceb88f54bc3f3d20d8be6f49e219a4/3934fa7c325d11924459d6793acd5cf7.jpeg',
      alt: 'Collaborative workspace'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % officeImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + officeImages.length) % officeImages.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Our Service Offerings
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive business services designed to support your US market entry and operations
          </p>
        </div>

        {/* Office Slideshow */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Our Professional Office Facilities
          </h3>
          <div className="relative max-w-5xl mx-auto">
            {/* Slideshow Container */}
            <div className="relative overflow-hidden rounded-xl shadow-2xl" style={{ height: '500px' }}>
              {officeImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent">
                    <div className="absolute bottom-8 left-8 text-white">
                      <p className="text-2xl font-semibold">{image.alt}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Previous Button */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 cursor-pointer"
              aria-label="Previous slide"
            >
              <i className="ri-arrow-left-s-line text-2xl text-gray-900"></i>
            </button>

            {/* Next Button */}
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 cursor-pointer"
              aria-label="Next slide"
            >
              <i className="ri-arrow-right-s-line text-2xl text-gray-900"></i>
            </button>

            {/* Dots Navigation */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {officeImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                    index === currentSlide
                      ? 'bg-white w-8'
                      : 'bg-white/50 hover:bg-white/75'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                  <i className={`${service.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{service.title}</h3>
              </div>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                {service.description}
              </p>
              
              <ul className="space-y-3">
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-gray-700">
                    <i className="ri-check-line text-green-500 mr-3"></i>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
